<template>
  <div
    class="h-screen flex w-full bg-img vx-row no-gutter items-center justify-center"
    id="page-login"
  >
    <div class="vx-col sm:w-1/2 md:w-1/2 lg:w-3/4 xl:w-3/5 sm:m-0 m-4" :class="{'mobile': isMobileDevice}">
      <vx-card>
        <div slot="no-body" class="full-page-bg-color">
          <div class="vx-row no-gutter justify-center items-center">
            <div class="vx-col hidden lg:block lg:w-1/2">
              <img
                src="@/assets/images/pages/login/logo_login.svg"
                alt="login"
                class="mx-auto"
              />
              <div class="version-text-box">
                <span
                  class="login-aisea-version-text"
                  v-if="version"
                  >Ver. {{ version }}</span
                >
              </div>
            </div>

            <div class="vx-col sm:w-full md:w-full lg:w-1/2 d-theme-dark-bg">
              <div class="p-8 login-tabs-container">
                <div class="vx-card__title mb-4">
                  <h4 class="mb-4">{{ $t("login.enterprise.title") }}</h4>
                  <p></p>
                </div>

                <div class="vx-row mb-6">
                  <div class="vx-col w-full">
                    <vs-input
                      class="w-full"
                      :label="$t('login.enterprise.id')"
                      v-model="enterpriseId"
                      v-validate="'required'"
                      name="enterpriseId"
                      :data-vv-as="$t('login.enterprise.id')"
                    /><span
                      class="text-danger text-sm"
                      v-show="errors.has('enterpriseId')"
                      >{{ errors.first("enterpriseId") }}</span
                    >
                  </div>
                </div>

                <div class="vx-row mb-6">
                  <div class="vx-col w-full">
                    <vs-input
                      autocomplete="username"
                      class="w-full"
                      :label="$t('login.user_id')"
                      v-model="userId"
                      v-validate="'required'"
                      name="userId"
                      :data-vv-as="$t('login.user_id')"
                      ref="userId"
                    /><span
                      class="text-danger text-sm"
                      v-show="errors.has('userId')"
                      >{{ errors.first("userId") }}</span
                    >
                  </div>
                </div>

                <div class="vx-row mb-6">
                  <div class="vx-col w-full">
                    <vs-input
                      autocomplete="new-password"
                      type="password"
                      class="w-full"
                      :label="$t('login.password')"
                      v-model="password"
                      v-validate="'required|min:8'"
                      name="password"
                      :data-vv-as="$t('login.password')"
                      ref="password"
                    /><span
                      class="text-danger text-sm"
                      v-show="errors.has('password')"
                      >{{ errors.first("password") }}</span
                    >
                  </div>
                </div>

                <div>
                  <div class="flex flex-wrap justify-between my-5">
                    <vs-checkbox v-model="rememberIds" class="mb-3"
                      >{{ $t("login.enterprise.remember_ids") }}
                    </vs-checkbox>
                    <router-link to="">{{
                      $t("login.enterprise.forget_password")
                    }}</router-link>
                  </div>
                  <div class="text-center">
                    <vs-button
                      ref="loadableButton"
                      id="login-button-with-loading"
                      class="text-center vs-con-loading__container"
                      @click="login"
                      :disabled="!validateForm || isLoggingIn"
                      >{{ $t("login.title") }}</vs-button
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapActions } = createNamespacedHelpers("auth");
import authService from "@/services/authService";
import environment from "@/environment/aisea";
import { DeviceInfoMixin } from "@/mixins";

const BAD_REQUEST = 400;

export default {
  mixins: [DeviceInfoMixin],
  data() {
    return {
      enterpriseId: "",
      userId: "",
      password: "",
      rememberIds: false,
      backgroundLoading: "primary",
      colorLoading: "#fff",
      version: environment.getVersion()  || '0.0.0',
      isLoggingIn: false,
    };
  },
  computed: {
    validateForm() {
      return !this.errors.any();
    },
    isMobileDevice() {
      return ["iPhone", "iPad"].includes(this.getDeviceInfo().deviceType);
    },
  },
  methods: {
    ...mapActions(["loginWithEnterprise"]),

    openLoadingContained() {
      this.$vs.loading({
        background: this.backgroundLoading,
        color: this.colorLoading,
        container: "#login-button-with-loading",
        scale: 0.45
      });
    },

    closeLoadingContained() {
      this.$vs.loading.close("#login-button-with-loading > .con-vs-loading");
    },

    async login() {
      // パスワード管理ツール使用時にstate更新されないのを防ぐ
      this.userId = this.$refs.userId.$refs.vsinput.value;
      this.password = this.$refs.password.$refs.vsinput.value;

      let result = this.$validator.validateAll();
      if (result) {
        this.isLoggingIn = true;
        this.openLoadingContained();
        const payload = {
          rememberIds: this.rememberIds,
          loginInfo: {
            enterprise_id: this.enterpriseId,
            id: this.userId,
            password: this.password
          }
        };
        try {
          const fbRes = await this.loginWithEnterprise(payload);
          if (fbRes) this.successLogin();
        } catch (e) {
          this.failureLogin(e);
        } finally {
          this.isLoggingIn = false;
        }
      }
    },

    successLogin() {
      this.closeLoadingContained();
      setTimeout(() => {
        this.$vs.notify({
          title: "ログインしました",
          iconPack: "feather",
          icon: "icon-check",
          color: "success"
        });
      }, 1000);
    },

    failureLogin(e) {
      this.closeLoadingContained();

      const title = e.statusCode === BAD_REQUEST && e.data && e.data.title
        ? e.data.title : "ログインに失敗しました";
      const message = e.statusCode === BAD_REQUEST && e.data && e.data.message
        ? e.data.message : "法人ID、ユーザーID、パスワードの入力に誤りがあります。";

      this.$vs.notify({
        title: title,
        text: message,
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "danger"
      });
    },

    notifyLogout() {
      if (this.$route.query.logout) {
        this.$vs.notify({
          title: "強制ログアウトしました",
          text:
            "ユーザーが削除されたか、または他のブラウザからログインした可能性があります。",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
          fixed: true
        });
      }
    },

    async setIds() {
      if (this.$route.params["enterpriseId"]) {
        this.enterpriseId = this.$route.params["enterpriseId"];
      } else {
        await this.setRememberIds();
      }
    },

    async setRememberIds() {
      let ids = await authService.getRememberIds();
      if (ids !== null) {
        this.rememberIds = true;
        this.userId = ids.userId;
        this.enterpriseId = ids.enterpriseId;
      }
    },
  },
  created() {
    this.setIds();
  },
  mounted() {
    this.notifyLogout();
  }
};
</script>

<style lang="scss">
#page-login {
  .vs-con-input-label.is-label-placeholder {
    margin-top: 30px;
  }

  .version-text-box {
    width: 300px;
    margin-right: auto;
    margin-left: auto;
  }

  .login-aisea-version-text {
    font-family: Montserrat;
    font-style: normal;
    font-weight: normal;
    font-size: 10px;
    color: white;
    float: right;
  }
  .mobile {
    .vs-input--input.normal {
      font-size: 16px !important;
    }
  }
}
</style>
